import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import "./App.css";
import { getCurrentUser } from "./utils/api";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VehiclesPage from "./pages/VehiclesPage";
import AssetDetailPage from "./pages/AssetDetailPage";
import MemberDashboard from "./pages/MemberDashboard";
import RegisterInterest from "./pages/RegisterInterest";
// import SupportPage from "./pages/SupportPage"; // Removed - Contact us now scrolls to footer
import AdminDashboard from "./pages/AdminDashboard";
import ComingSoon from "./pages/ComingSoon";
import HowItWorksPage from "./pages/HowItWorksPage";
import WhoIsItForPage from "./pages/WhoIsItForPage";
import AboutUsPage from "./pages/AboutUsPage";
import HighAssetsPage from "./pages/HighAssetsPage";
import CurationDetailPage from "./pages/CurationDetailPage";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import TransactionPage from "./pages/TransactionPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage";

// Components
import ConciergeButton from "./components/ConciergeButton";
import LiliWelcomePopup from "./components/LiliWelcomePopup";
import ScrollToTop from "./components/ScrollToTop";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      console.log('[Auth] Checking authentication...');
      console.log('[Auth] Cookies enabled:', navigator.cookieEnabled);
      const response = await getCurrentUser();
      console.log('[Auth] ✓ User authenticated:', response.data.user.email);
      console.log('[Auth] Profile completed:', response.data.user.profile_completed);
      setUser(response.data.user);
    } catch (error) {
      console.log('[Auth] ✗ Not authenticated:', error.response?.status, error.response?.data?.detail || '');
      if (error.response?.status === 401) {
        console.log('[Auth] Session invalid or expired');
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        {/* Scroll to top on route change */}
        <ScrollToTop />
        
        <Routes>
          <Route path="/" element={<HomePage user={user} />} />
          <Route path="/about-us" element={<AboutUsPage user={user} />} />
          <Route path="/how-it-works" element={<HowItWorksPage user={user} />} />
          <Route path="/who-is-it-for" element={<WhoIsItForPage user={user} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage user={user} />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage user={user} />} />
          <Route path="/login" element={<LoginPage setUser={setUser} />} />
          <Route path="/signup" element={<SignupPage setUser={setUser} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/high-assets" element={<HighAssetsPage user={user} />} />
          <Route path="/curation/:packageId" element={<CurationDetailPage user={user} />} />
          <Route path="/high-assets/vehicles" element={<VehiclesPage user={user} />} />
          <Route path="/high-assets/jets" element={<ComingSoon title="Private Jets" user={user} />} />
          <Route path="/high-assets/homes" element={<ComingSoon title="Vacation Homes" user={user} />} />
          <Route path="/high-luxury" element={<ComingSoon title="High Luxury" user={user} />} />
          <Route path="/high-life" element={<ComingSoon title="High Life" user={user} />} />
          <Route path="/asset/:id" element={<AssetDetailPage user={user} />} />
          <Route path="/transaction/:assetId" element={<TransactionPage user={user} setUser={setUser} />} />
          <Route path="/register-interest" element={<RegisterInterest user={user} />} />
          {/* Support page removed - Contact us now scrolls to footer */}
          <Route 
            path="/complete-profile" 
            element={user ? <CompleteProfilePage user={user} setUser={setUser} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/member-dashboard" 
            element={user ? (user.profile_completed ? <MemberDashboard user={user} setUser={setUser} /> : <Navigate to="/complete-profile" />) : <Navigate to="/login" />} 
          />
          <Route 
            path="/admin" 
            element={user && user.role === 'admin' ? <AdminDashboard user={user} setUser={setUser} /> : <Navigate to="/login" />} 
          />
        </Routes>
        
        {/* Concierge Button - Available on all pages */}
        <ConciergeButton user={user} />
        
        {/* Lili Welcome Popup - Shows once on first visit */}
        <LiliWelcomePopup />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
